/**
 * Hook for managing application stability - prevents race conditions,
 * handles loading states, and provides safe interaction patterns
 */

import { useCallback, useRef, useState, useEffect } from 'react'
import { 
  debounce, 
  debounceAsync, 
  requestDeduplicator, 
  createClickProtection,
  globalLoadingManager 
} from '../utils/debounce'
import { logInfo, logError, logWarning } from '../utils/errorLogger'

interface StabilityConfig {
  debounceMs?: number
  requestTtl?: number
  clickCooldown?: number
  maxConcurrentRequests?: number
}

interface StabilityState {
  isLoading: boolean
  pendingRequests: number
  lastAction: string | null
  errors: string[]
}

export function useStabilityManager(
  componentName: string,
  config: StabilityConfig = {}
) {
  const {
    debounceMs = 300,
    requestTtl = 5000,
    clickCooldown = 1000,
    maxConcurrentRequests = 3
  } = config

  const [state, setState] = useState<StabilityState>({
    isLoading: false,
    pendingRequests: 0,
    lastAction: null,
    errors: []
  })

  const pendingRequestsRef = useRef(new Set<string>())
  const lastActionTimeRef = useRef<number>(0)

  // Update loading state when global loading manager changes
  useEffect(() => {
    const unsubscribe = globalLoadingManager.subscribe((states) => {
      const componentLoading = Object.keys(states).some(key => 
        key.startsWith(componentName) && states[key]
      )
      setState(prev => ({ ...prev, isLoading: componentLoading }))
    })
    return unsubscribe
  }, [componentName])

  /**
   * Safe API request wrapper with deduplication and loading management
   */
  const safeRequest = useCallback(async <T>(
    requestKey: string,
    requestFn: () => Promise<T>,
    options: { 
      skipDeduplication?: boolean
      timeout?: number
      retries?: number
    } = {}
  ): Promise<T> => {
    const {
      skipDeduplication = false,
      timeout = requestTtl,
      retries = 0
    } = options

    const fullRequestKey = `${componentName}-${requestKey}`

    // Check concurrent request limit
    if (pendingRequestsRef.current.size >= maxConcurrentRequests) {
      const error = `Maximum concurrent requests (${maxConcurrentRequests}) exceeded for ${componentName}`
      logWarning(error, componentName)
      throw new Error(error)
    }

    // Set loading state
    globalLoadingManager.setLoading(fullRequestKey, true)
    pendingRequestsRef.current.add(fullRequestKey)

    setState(prev => ({
      ...prev,
      pendingRequests: pendingRequestsRef.current.size,
      lastAction: requestKey
    }))

    logInfo(`🔄 Starting safe request: ${requestKey}`, componentName)

    try {
      let result: T

      if (skipDeduplication) {
        result = await requestFn()
      } else {
        result = await requestDeduplicator.deduplicate(
          fullRequestKey,
          requestFn,
          timeout
        )
      }

      logInfo(`✅ Safe request completed: ${requestKey}`, componentName)
      return result

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logError(`❌ Safe request failed: ${requestKey} - ${errorMessage}`, componentName)

      // Retry logic
      if (retries > 0 && !errorMessage.includes('cancelled')) {
        logInfo(`🔄 Retrying request: ${requestKey} (${retries} retries left)`, componentName)
        await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1s before retry
        return safeRequest(requestKey, requestFn, { ...options, retries: retries - 1 })
      }

      setState(prev => ({
        ...prev,
        errors: [...prev.errors.slice(-4), errorMessage] // Keep last 5 errors
      }))

      throw error

    } finally {
      // Cleanup
      globalLoadingManager.setLoading(fullRequestKey, false)
      pendingRequestsRef.current.delete(fullRequestKey)

      setState(prev => ({
        ...prev,
        pendingRequests: pendingRequestsRef.current.size
      }))
    }
  }, [componentName, maxConcurrentRequests, requestTtl])

  /**
   * Safe click handler with protection against rapid clicking
   */
  const safeClick = useCallback((
    actionName: string,
    handler: () => void | Promise<void>,
    options: { cooldown?: number } = {}
  ) => {
    const { cooldown = clickCooldown } = options

    return createClickProtection(async () => {
      const now = Date.now()
      
      // Additional component-level cooldown check
      if (now - lastActionTimeRef.current < cooldown) {
        logWarning(`Click ignored: ${actionName} still in cooldown`, componentName)
        return
      }

      lastActionTimeRef.current = now
      setState(prev => ({ ...prev, lastAction: actionName }))

      logInfo(`🖱️ Safe click: ${actionName}`, componentName)

      try {
        await handler()
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        logError(`❌ Safe click failed: ${actionName} - ${errorMessage}`, componentName)
        
        setState(prev => ({
          ...prev,
          errors: [...prev.errors.slice(-4), errorMessage]
        }))
      }
    }, cooldown)
  }, [componentName, clickCooldown])

  /**
   * Debounced function creator
   */
  const createDebouncedFn = useCallback(<T extends (...args: any[]) => any>(
    fn: T,
    delay: number = debounceMs,
    immediate: boolean = false
  ) => {
    return debounce(fn, delay, immediate)
  }, [debounceMs])

  /**
   * Debounced async function creator
   */
  const createDebouncedAsyncFn = useCallback(<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    delay: number = debounceMs
  ) => {
    return debounceAsync(fn, delay)
  }, [debounceMs])

  /**
   * Navigation safety - prevents rapid navigation changes
   */
  const safeNavigate = useCallback((
    destination: string,
    navigationFn: () => void
  ) => {
    const navigationKey = `navigate-${destination}`
    
    return safeClick(navigationKey, navigationFn, { cooldown: 500 })
  }, [safeClick])

  /**
   * Tab management safety - prevents duplicate tab creation
   */
  const safeTabAction = useCallback((
    action: string,
    tabId: string,
    actionFn: () => void
  ) => {
    const tabActionKey = `tab-${action}-${tabId}`
    
    return safeClick(tabActionKey, actionFn, { cooldown: 300 })
  }, [safeClick])

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setState(prev => ({ ...prev, errors: [] }))
  }, [])

  /**
   * Force clear all pending requests (emergency cleanup)
   */
  const emergencyCleanup = useCallback(() => {
    logWarning('🚨 Emergency cleanup triggered', componentName)
    
    // Clear all pending requests for this component
    Array.from(pendingRequestsRef.current).forEach(requestKey => {
      globalLoadingManager.setLoading(requestKey, false)
    })
    
    pendingRequestsRef.current.clear()
    requestDeduplicator.clear()
    
    setState({
      isLoading: false,
      pendingRequests: 0,
      lastAction: null,
      errors: []
    })
  }, [componentName])

  /**
   * Get stability metrics for debugging
   */
  const getMetrics = useCallback(() => {
    return {
      componentName,
      ...state,
      globalPendingRequests: requestDeduplicator.getPendingCount(),
      config: {
        debounceMs,
        requestTtl,
        clickCooldown,
        maxConcurrentRequests
      }
    }
  }, [componentName, state, debounceMs, requestTtl, clickCooldown, maxConcurrentRequests])

  return {
    // State
    state,
    
    // Safe interaction methods
    safeRequest,
    safeClick,
    safeNavigate,
    safeTabAction,
    
    // Utility functions
    createDebouncedFn,
    createDebouncedAsyncFn,
    
    // Management functions
    clearErrors,
    emergencyCleanup,
    getMetrics,
    
    // Computed properties
    isStable: state.pendingRequests === 0 && !state.isLoading,
    hasErrors: state.errors.length > 0,
    canInteract: state.pendingRequests < maxConcurrentRequests
  }
}
